import { useState } from "react";
import { format } from "date-fns";
import { CalendarDays, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const toDate = (ymd) => {
  if (!ymd) return null;
  const [y, m, d] = ymd.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toYmd = (dt) => format(dt, "yyyy-MM-dd");
export const fmtPaidDate = (ymd) => (ymd ? format(toDate(ymd), "MMM d, yyyy") : "—");

export const canEditPaymentDates = (user) =>
  !!user && (user.is_super_admin || ["owner", "admin"].includes(user.role));

export const PaymentDateEditor = ({ invoiceId, payment, onSaved, disabled }) => {
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);
  const current = payment.paid_date;
  const editable = !disabled && canEditPaymentDates(user);

  const label = (
    <span className="font-mono-accent text-zinc-700" data-testid={`payment-date-${payment.id}`}>
      {fmtPaidDate(current)}
      {payment.paid_at_manual && (
        <span className="ml-1 text-[10px] uppercase tracking-widest text-amber-600" title="Date edited by an admin" data-testid={`payment-date-edited-${payment.id}`}>edited</span>
      )}
    </span>
  );
  if (!editable) return label;

  const save = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/invoices/${invoiceId}/payments/${payment.id}/paid-date`, { paid_date: toYmd(picked) });
      toast.success(data.unchanged ? "Date unchanged" : `Payment date set to ${fmtPaidDate(data.paid_date)}`);
      setOpen(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update payment date");
    } finally {
      setSaving(false);
    }
  };

  const today = toDate(payment.max_date) || new Date();
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setPicked(toDate(current)); }}>
      <PopoverTrigger asChild>
        <button type="button" className="group inline-flex items-center gap-1.5 rounded px-1 -mx-1 hover:bg-orange-50 transition-colors" title="Change the date this payment was received" data-testid={`payment-date-edit-btn-${payment.id}`}>
          {label}
          <CalendarDays className="w-3.5 h-3.5 text-zinc-400 group-hover:text-[#FF5A00]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={`payment-date-popover-${payment.id}`}>
        <div className="px-3 pt-3 text-xs font-mono-accent uppercase tracking-widest text-zinc-500">Date received</div>
        <Calendar
          mode="single"
          selected={picked}
          onSelect={(d) => d && setPicked(d)}
          classNames={{
            day_selected: "!bg-[#FF5A00] !text-white hover:!bg-[#e65100] focus:!bg-[#FF5A00] font-semibold",
            day_today: "ring-1 ring-zinc-300",
          }}
          defaultMonth={picked || today}
          disabled={(d) => d > today}
          initialFocus
        />
        <div className="flex items-center justify-between gap-2 border-t border-zinc-100 px-3 py-2">
          <span className="text-xs font-semibold text-zinc-800" data-testid={`payment-date-picked-${payment.id}`}>{picked ? fmtPaidDate(toYmd(picked)) : "Pick a date"}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid={`payment-date-cancel-${payment.id}`}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={!picked || saving} className="bg-[#FF5A00] hover:bg-[#e65100] text-white" data-testid={`payment-date-save-${payment.id}`}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
